import { AlertTriangle, Bell, Check, ChevronRight, Info } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import {
  useClearAlerts,
  useTrayAlerts,
  useUpdateAlertStatus,
} from "@/features/alert/hooks";
import type { Alert, AlertCounts } from "@/features/alert/types";
import { NotificationPreviewRow } from "./NotificationPreviewRow";
import { getAlertDestination, getAlertTraySections } from "./alertCardRules";

type Feedback = {
  title: string;
  message?: string;
  tone: "error" | "success";
};

export default function NotificationPanel({
  open,
  id,
  onClose,
  onFeedback,
  counts,
  placement = "dropdown",
}: {
  open: boolean;
  id: string;
  onClose: () => void;
  onFeedback: (feedback: Feedback) => void;
  counts?: AlertCounts;
  placement?: "dropdown" | "viewport";
}) {
  const navigate = useNavigate();
  const clearRef = useRef<HTMLButtonElement>(null);
  const allRef = useRef<HTMLButtonElement>(null);
  const { data: currentUser } = useCurrentUser();
  const trayQuery = useTrayAlerts({ view: "popup", limit: 7, offset: 0 });
  const clearAlerts = useClearAlerts();
  const updateStatus = useUpdateAlertStatus();
  const { actionable, newAlerts } = getAlertTraySections(
    trayQuery.data?.items ?? [],
  );
  const isEmpty = actionable.length === 0 && newAlerts.length === 0;
  const canClear = (counts?.clearable_count ?? 0) > 0;

  useEffect(() => {
    if (!open) return;
    const target =
      placement === "viewport" || !canClear ? allRef.current : clearRef.current;
    (target?.disabled ? allRef.current : target)?.focus();
  }, [open, placement]);

  function openNotificationsPage() {
    onClose();
    navigate({ to: "/mais/notificacoes" });
  }

  async function openAlert(alert: Alert) {
    try {
      if (alert.status === "sent") {
        await updateStatus.mutateAsync({ id: alert.id, status: "seen" });
        onFeedback({
          title: "Notificação marcada como vista",
          tone: "success",
        });
      }
      const destination = getAlertDestination(alert, currentUser?.role);
      onClose();
      navigate(destination ?? { to: "/mais/notificacoes" });
    } catch (error) {
      onFeedback({
        title: "Não foi possível abrir a notificação",
        message: getUserFacingErrorMessage(
          error,
          "Tente novamente em alguns instantes.",
        ),
        tone: "error",
      });
    }
  }

  function clearAll() {
    clearAlerts.mutate("all", {
      onSuccess: ({ updated }) => {
        allRef.current?.focus();
        onFeedback({
          title: "Notificações limpas",
          message:
            updated === 1
              ? "1 notificação atualizada."
              : `${updated} notificações atualizadas.`,
          tone: "success",
        });
      },
      onError: (error) =>
        onFeedback({
          title: "Não foi possível limpar as notificações",
          message: getUserFacingErrorMessage(
            error,
            "Tente novamente em alguns instantes.",
          ),
          tone: "error",
        }),
    });
  }

  return (
    <section
      id={id}
      className="notification-tray"
      data-testid="notification-panel"
      data-motion={open ? "open" : "closed"}
      data-placement={placement}
      aria-label="Notificações"
      aria-hidden={!open}
      aria-busy={trayQuery.isFetching || clearAlerts.isPending}
      inert={!open || undefined}
    >
      <header className="notification-tray__header">
        <button
          ref={allRef}
          type="button"
          className="notification-tray__all"
          onClick={openNotificationsPage}
        >
          Ver todas <ChevronRight size={15} aria-hidden="true" />
        </button>
      </header>

      <div className="notification-tray__scroll">
        {trayQuery.isLoading ? (
          <div className="notification-tray__state" role="status">
            <Bell size={22} aria-hidden="true" />
            <strong>Carregando notificações...</strong>
          </div>
        ) : trayQuery.error ? (
          <div className="notification-tray__state" role="alert">
            <AlertTriangle size={22} aria-hidden="true" />
            <strong>Não foi possível carregar as notificações</strong>
            <span>
              {getUserFacingErrorMessage(
                trayQuery.error,
                "Tente novamente em alguns instantes.",
              )}
            </span>
            <button
              type="button"
              onClick={() => void trayQuery.refetch()}
              disabled={trayQuery.isFetching}
            >
              {trayQuery.isFetching ? "Tentando..." : "Tentar novamente"}
            </button>
          </div>
        ) : isEmpty ? (
          <div className="notification-tray__state">
            <Check size={22} aria-hidden="true" />
            <strong>Tudo limpo</strong>
            <span>Nenhuma notificação precisa da sua atenção.</span>
          </div>
        ) : (
          <>
            {actionable.length > 0 ? (
              <section
                className="notification-tray__section"
                data-tone="action"
              >
                <div className="notification-tray__section-header">
                  <span>
                    <AlertTriangle size={15} aria-hidden="true" /> Ações
                    necessárias
                  </span>
                  <b>{counts?.actionable_count ?? actionable.length}</b>
                </div>
                <div className="notification-tray__list">
                  {actionable.map((alert) => (
                    <NotificationPreviewRow
                      key={alert.id}
                      alert={alert}
                      onSelect={() => void openAlert(alert)}
                      isPending={updateStatus.isPending}
                    />
                  ))}
                </div>
              </section>
            ) : null}
            {newAlerts.length > 0 ? (
              <section className="notification-tray__section" data-tone="new">
                <div className="notification-tray__section-header">
                  <span>
                    <Info size={15} aria-hidden="true" /> Novas
                  </span>
                  <b>{counts?.unread_count ?? newAlerts.length}</b>
                </div>
                <div className="notification-tray__list">
                  {newAlerts.map((alert) => (
                    <NotificationPreviewRow
                      key={alert.id}
                      alert={alert}
                      onSelect={() => void openAlert(alert)}
                      isPending={updateStatus.isPending}
                    />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>

      {canClear ? (
        <footer className="notification-tray__footer">
          <button
            ref={clearRef}
            type="button"
            className="notification-tray__clear"
            onClick={clearAll}
            disabled={clearAlerts.isPending}
          >
            <Check size={14} aria-hidden="true" />
            {clearAlerts.isPending ? "Limpando..." : "Limpar"}
          </button>
        </footer>
      ) : null}
    </section>
  );
}
